import React, { useState } from 'react';
import './ZillowScraper.css';

const ZillowScraper = () => {
  const [searchUrl, setSearchUrl] = useState('');
  const [isScrapingActive, setIsScrapingActive] = useState(false);
  const [scrapingStats, setScrapingStats] = useState({
    currentPage: 0,
    totalPages: 0,
    listingsFound: 0,
    estimatedTotal: 0,
    status: 'idle'
  });
  const [scrapedData, setScrapedData] = useState([]);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);

  const startScraping = async () => {
    if (!searchUrl.trim()) {
      setError('Please enter a Zillow search URL');
      return;
    }

    setError(null);
    setIsScrapingActive(true);
    setScrapingStats({
      currentPage: 0,
      totalPages: 0,
      listingsFound: 0,
      estimatedTotal: 0,
      status: 'initializing'
    });
    setScrapedData([]);

    try {
      // Start scraping process
      const response = await fetch('/api/zillow-scraper', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          searchUrl: searchUrl.trim(),
          action: 'start'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Start polling for updates
        pollScrapingProgress();
      } else {
        throw new Error(result.error || 'Failed to start scraping');
      }

    } catch (error) {
      console.error('Scraping error:', error);
      setError(error.message);
      setIsScrapingActive(false);
    }
  };

  const pollScrapingProgress = async () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch('/api/zillow-scraper', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'status'
          })
        });

        const result = await response.json();

        if (result.stats) {
          setScrapingStats(result.stats);
        }

        if (result.data && result.data.length > 0) {
          setScrapedData(result.data);
        }

        if (result.logs && result.logs.length > 0) {
          setLogs(result.logs);
        }

        // Check if scraping is complete
        if (result.stats.status === 'completed' || result.stats.status === 'error') {
          clearInterval(pollInterval);
          setIsScrapingActive(false);
          
          if (result.stats.status === 'error') {
            setError(result.error || 'Scraping failed');
          }
        }

      } catch (error) {
        console.error('Polling error:', error);
        clearInterval(pollInterval);
        setIsScrapingActive(false);
        setError('Connection lost during scraping');
      }
    }, 2000); // Poll every 2 seconds
  };

  const stopScraping = async () => {
    try {
      await fetch('/api/zillow-scraper', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'stop'
        })
      });
    } catch (error) {
      console.error('Stop scraping error:', error);
    }
    setIsScrapingActive(false);
  };

  const downloadCSV = () => {
    if (scrapedData.length === 0) {
      alert('No data to download');
      return;
    }

    // Create CSV content
    const headers = [
      'Address',
      'City', 
      'State',
      'ZIP',
      'Sold Price',
      'Sold Date',
      'Original List Price',
      'Beds',
      'Baths',
      'Square Feet',
      'Lot Size',
      'Property Type',
      'Agent Name',
      'Agent Phone',
      'Agent Office',
      'Broker',
      'ZPID',
      'Zillow URL',
      'Days on Market'
    ];

    const csvContent = [
      headers.join(','),
      ...scrapedData.map(listing => [
        `"${listing.address || ''}"`,
        `"${listing.city || ''}"`,
        `"${listing.state || ''}"`,
        `"${listing.zip || ''}"`,
        listing.soldPrice || '',
        `"${listing.soldDate || ''}"`,
        listing.originalPrice || '',
        listing.beds || '',
        listing.baths || '',
        listing.sqft || '',
        `"${listing.lotSize || ''}"`,
        `"${listing.propertyType || ''}"`,
        `"${listing.agentName || ''}"`,
        `"${listing.agentPhone || ''}"`,
        `"${listing.agentOffice || ''}"`,
        `"${listing.broker || ''}"`,
        listing.zpid || '',
        `"${listing.zillowUrl || ''}"`,
        listing.daysOnMarket || ''
      ].join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zillow_listings_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="zillow-scraper">
      <div className="scraper-header">
        <h2>🏠 Zillow Listings Scraper</h2>
        <p>Paste any Zillow search URL to extract all listings with agent contact info</p>
      </div>

      <div className="search-section">
        <div className="url-input-group">
          <input
            type="url"
            value={searchUrl}
            onChange={(e) => setSearchUrl(e.target.value)}
            placeholder="Paste Zillow search URL here..."
            disabled={isScrapingActive}
            className="url-input"
          />
          <div className="action-buttons">
            {!isScrapingActive ? (
              <button 
                onClick={startScraping}
                className="start-button"
                disabled={!searchUrl.trim()}
              >
                🚀 Start Scraping
              </button>
            ) : (
              <button 
                onClick={stopScraping}
                className="stop-button"
              >
                ⏹️ Stop Scraping
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="error-message">
            ❌ {error}
          </div>
        )}
      </div>

      {(isScrapingActive || scrapingStats.status !== 'idle') && (
        <div className="progress-section">
          <div className="progress-header">
            <h3>📊 Scraping Progress</h3>
            <div className={`status-badge status-${scrapingStats.status}`}>
              {scrapingStats.status.charAt(0).toUpperCase() + scrapingStats.status.slice(1)}
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{scrapingStats.currentPage}</div>
              <div className="stat-label">Current Page</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{scrapingStats.totalPages || '?'}</div>
              <div className="stat-label">Total Pages</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{scrapingStats.listingsFound}</div>
              <div className="stat-label">Listings Found</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{scrapingStats.estimatedTotal || '?'}</div>
              <div className="stat-label">Estimated Total</div>
            </div>
          </div>

          <div className="progress-bar-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{
                  width: scrapingStats.totalPages > 0 
                    ? `${(scrapingStats.currentPage / scrapingStats.totalPages) * 100}%`
                    : '0%'
                }}
              ></div>
            </div>
            <div className="progress-text">
              {scrapingStats.totalPages > 0 
                ? `${Math.round((scrapingStats.currentPage / scrapingStats.totalPages) * 100)}%`
                : '0%'
              } Complete
            </div>
          </div>

          {isScrapingActive && (
            <div className="scraping-animation">
              <div className="spinner"></div>
              <span>Scraping in progress... This may take several minutes</span>
            </div>
          )}

          {logs.length > 0 && (
            <div className="logs-section">
              <h4>🔍 Detailed Progress Logs</h4>
              <div className="logs-container">
                {logs.slice(-10).map((log, index) => (
                  <div key={index} className="log-entry">
                    {log}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {scrapedData.length > 0 && (
        <div className="results-section">
          <div className="results-header">
            <h3>📋 Scraped Results ({scrapedData.length} listings)</h3>
            <button 
              onClick={downloadCSV}
              className="download-button"
            >
              📥 Download CSV
            </button>
          </div>

          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Price</th>
                  <th>Sold Date</th>
                  <th>Beds/Baths</th>
                  <th>Agent</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scrapedData.slice(0, 50).map((listing, index) => (
                  <tr key={index}>
                    <td>
                      <div className="address-cell">
                        <div className="address-main">{listing.address}</div>
                        <div className="address-sub">{listing.city}, {listing.state} {listing.zip}</div>
                      </div>
                    </td>
                    <td>
                      <div className="price-cell">
                        <div className="sold-price">${listing.soldPrice?.toLocaleString()}</div>
                        {listing.originalPrice && (
                          <div className="original-price">List: ${listing.originalPrice?.toLocaleString()}</div>
                        )}
                      </div>
                    </td>
                    <td>{listing.soldDate}</td>
                    <td>{listing.beds}bd / {listing.baths}ba</td>
                    <td>{listing.agentName || 'N/A'}</td>
                    <td>{listing.agentPhone || 'N/A'}</td>
                    <td>
                      <a 
                        href={listing.zillowUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="view-link"
                      >
                        View
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {scrapedData.length > 50 && (
              <div className="table-footer">
                Showing first 50 results. Download CSV for complete data.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZillowScraper;